/**
 * Палитра конструктора паспорта строится из новой нормализованной модели полей.
 */
import { computed, ref, type Ref } from 'vue'
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import type { ObjectTypeGroup } from '@/entities/Inventory/ObjectSubTypes'
import type { GridFieldDragType } from '@/features/Inventory/ObjectTypePassportGrid/model/types'
import type { PaletteField, PaletteGroup, PaletteGroupKind } from './types'
import {
  catalogFieldTypesFromClasses,
  objectTypeFieldsToInventoryFields,
  primitiveFieldTypesFromClasses,
  referenceFieldTypesFromClasses,
  type ObjectTypeFieldsResponse,
} from './objectTypeFields'

const OWN_GROUP_ID = 'palette-own'
const CATALOG_GROUP_ID = 'palette-catalog'
const REFERENCE_GROUP_ID = 'palette-reference'

/** Auto-generated relation table (e.g. one_to_many target side) — shown under «Перечни», not own fields. */
export function isAutoRelationTableField(f: ObjectInventoryField): boolean {
  return Boolean(f.is_table) && Number(f.link_target_type_id) > 0
}

function toFieldDrag(f: ObjectInventoryField, source: PaletteGroupKind): GridFieldDragType {
  const listGroupId =
    source === 'list' && f.group != null && Number.isFinite(Number(f.group))
      ? Number(f.group)
      : undefined

  return {
    id: listGroupId != null ? `list:${listGroupId}#f:${f.id}` : f.id,
    type: 'field',
    name: f.name,
    inventory_type: f.object_type,
    palette_source: source,
    ...(listGroupId != null ? { list_group_id: listGroupId, inventory_field_id: f.id } : {}),
    ...(f.link_target_type_id != null ? { link_field_id: f.id } : {}),
  }
}

function paletteFieldFromInventory(
  f: ObjectInventoryField,
  source: PaletteGroupKind,
): PaletteField {
  return {
    id: `field-${f.id}`,
    displayName: f.name,
    field: f,
    drag: toFieldDrag(f, source),
  }
}

function matchesQuery(q: string, ...haystacks: string[]) {
  const needle = q.trim().toLowerCase()
  if (!needle) return true
  return haystacks.some((h) => h.toLowerCase().includes(needle))
}

function applyPaletteSearch(
  group: PaletteGroup,
  query: string,
  openOverrides: Record<string, boolean>,
): PaletteGroup {
  const searchActive = Boolean(query.trim())

  return {
    ...group,
    open: searchActive ? true : openOverrides[group.id] ?? group.open,
    fields: group.fields.filter((field) =>
      matchesQuery(query, group.title, field.displayName),
    ),
  }
}

function sortObjectTypeFields<T extends { sort: number; name: string }>(fields: T[]): T[] {
  return [...fields].sort((a, b) => {
    const sortDiff = (Number(a.sort) || 0) - (Number(b.sort) || 0)
    if (sortDiff !== 0) return sortDiff
    return a.name.localeCompare(b.name)
  })
}

export function usePalette(
  fieldGroups: Ref<ObjectTypeGroup[]>,
  objectTypeFieldsResponse: Ref<ObjectTypeFieldsResponse>,
  objectTypeFieldsLoading: Ref<boolean>,
  refetchObjectTypeFields: () => Promise<unknown>,
) {
  const searchQuery = ref('')
  const openOverrides = ref<Record<string, boolean>>({})

  const primitiveTypes = computed(() =>
    primitiveFieldTypesFromClasses(objectTypeFieldsResponse.value.fieldClasses),
  )
  const catalogTypes = computed(() =>
    catalogFieldTypesFromClasses(objectTypeFieldsResponse.value.fieldClasses),
  )
  const referenceTypes = computed(() =>
    referenceFieldTypesFromClasses(objectTypeFieldsResponse.value.fieldClasses),
  )

  const primitiveTypeIds = computed(() => new Set(primitiveTypes.value.map((item) => item.id)))
  const catalogTypeIds = computed(() => new Set(catalogTypes.value.map((item) => item.id)))
  const referenceTypeIds = computed(() => new Set(referenceTypes.value.map((item) => item.id)))
  const listGroupIds = computed(
    () =>
      new Set(
        fieldGroups.value
          .filter((group) => String(group.type ?? '').toUpperCase() === 'LIST')
          .map((group) => String(group.id)),
      ),
  )

  const withoutListGroupFields = (fields: ObjectInventoryField[]): ObjectInventoryField[] =>
    fields.filter((field) => field.group == null || !listGroupIds.value.has(String(field.group)))

  const ownFields = computed(() =>
    withoutListGroupFields(
      objectTypeFieldsToInventoryFields(
        sortObjectTypeFields(
          objectTypeFieldsResponse.value.fields.filter((field) =>
            primitiveTypeIds.value.has(field.field_type),
          ),
        ),
        primitiveTypes.value,
      ),
    ),
  )

  const catalogFields = computed(() =>
    withoutListGroupFields(
      objectTypeFieldsToInventoryFields(
        sortObjectTypeFields(
          objectTypeFieldsResponse.value.fields.filter((field) =>
            catalogTypeIds.value.has(field.field_type) || Number(field.catalog_id) > 0,
          ),
        ),
        catalogTypes.value,
      ),
    ),
  )

  const referenceFields = computed(() =>
    withoutListGroupFields(
      objectTypeFieldsToInventoryFields(
        sortObjectTypeFields(
          objectTypeFieldsResponse.value.fields.filter((field) =>
            referenceTypeIds.value.has(field.field_type) || Number(field.reference_id) > 0,
          ),
        ),
        referenceTypes.value,
      ),
    ),
  )

  const baseGroups = computed<PaletteGroup[]>(() => [
    {
      id: OWN_GROUP_ID,
      title: 'Собственные поля',
      kind: 'own',
      open: true,
      fields: ownFields.value.map((field) => paletteFieldFromInventory(field, 'own')),
    },
    {
      id: CATALOG_GROUP_ID,
      title: 'Справочники',
      kind: 'parent',
      open: false,
      fields: catalogFields.value.map((field) => paletteFieldFromInventory(field, 'parent')),
    },
    {
      id: REFERENCE_GROUP_ID,
      title: 'Связи',
      kind: 'element',
      open: false,
      fields: referenceFields.value.map((field) => paletteFieldFromInventory(field, 'element')),
    },
  ])

  const groups = computed(() =>
    baseGroups.value.map((group) =>
      applyPaletteSearch(group, searchQuery.value, openOverrides.value),
    ),
  )

  const toggleGroup = (id: string) => {
    const base = baseGroups.value.find((group) => group.id === id)
    const defaultOpen = base?.open ?? false
    const current = openOverrides.value[id] ?? defaultOpen
    openOverrides.value = { ...openOverrides.value, [id]: !current }
  }

  return {
    searchQuery,
    groups,
    isLoading: objectTypeFieldsLoading,
    refetch: refetchObjectTypeFields,
    toggleGroup,
  }
}
